import { createFileRoute } from '@tanstack/react-router';
import * as z from 'zod';
import { PromotionsPage } from '@/pages/menu/promotions';
import { requireAuth } from '../require-auth';
import { menuSearchSchema } from '../menu-search';
import { promotionWeek } from '../promotion-week';

/**
 * The promotions section page. Keeps the legacy `?category=` alias alongside
 * the shared schema's `categoryId`, so links written against the old
 * `/eats/menu/promotions` search schema keep filtering instead of landing
 * unfiltered.
 */
const promotionsSearchSchema = menuSearchSchema
  .extend({ category: z.string().optional().catch(undefined) })
  .transform((search) =>
    search.from === undefined && search.to === undefined
      ? { ...search, ...promotionWeek() }
      : search,
  );

// No loader prefetch: the list is keyed by the active branch, which is only
// known once the branch list has loaded (see `useMerchantScope`). Prefetching
// here would either fire with an empty scope or duplicate the branch request,
// so the page's own query owns the fetch.
export const Route = createFileRoute('/eats/menu_/promotions')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: promotionsSearchSchema,
  component: PromotionsRoute,
});

function PromotionsRoute() {
  const {
    search,
    status,
    categoryId,
    category,
    branchId,
    allBranches,
    from,
    to,
    showcase,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <PromotionsPage
      search={search}
      status={status}
      categoryId={categoryId}
      category={category}
      branchId={branchId}
      allBranches={allBranches ?? true}
      from={from}
      to={to}
      showcase={showcase ?? false}
      onSelectAllBranches={(next) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            allBranches: next ? undefined : false,
            branchId: undefined,
          }),
        });
      }}
      onSelectBranch={(id) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            branchId: id,
            allBranches: false,
          }),
        });
      }}
      onToggleShowcase={() => {
        void navigate({
          search: (prev) => ({
            ...prev,
            showcase: prev.showcase ? undefined : true,
          }),
        });
      }}
    />
  );
}
